// © Microsoft Corporation. All rights reserved.

import {
    ITrackInfo,
    IWidgetInstance,
    KeyValueArray,
    KeyValuePair,
    UrlSearchParamsHelper,
    Utility,
    clientId,
    getAppEnvironment,
    getHeadData,
    getMuidCookie,
    headData,
    oneServiceFriendlyName,
    oneServiceHeaders,
    oneServiceQueryStringKeys,
    oneServiceScope,
    setMuidCookie,
    winWidClientIdAad,
    winWidClientIdMsa,
    winWidResourceAad,
    winWidResourceMsa,
    winWidScopeAad,
    winWidScopeMsa
} from "@msnews/core";
import { aadTokenQsKey, aadTokenQsVal, getAadData, getWindowsWidgetMuid, getWindowsWidgetToken, isGccModUser, isUserSignedIn, useEnterpriseCompliance } from "@msnews/auth";
import { canUseDOM, getRequestData } from "@msnews/isomorphic-util";

import { AudienceModeType } from "@msnews/experiences-constants";
import { isString } from "lodash-es";
import { Telemetry } from "@msnews/telemetry-contracts";
import { FailedToRetrieveAuthTokenFromWDXClient, FailedToRetrieveMUIDFromWDXClient, FailedToRetrieveUserAuthTokenAndMUID } from "@msnews/diagnostics";

/**
 * A namespace that provides OneService utility functionality
 */
export namespace OneServiceUtility {

    /**
     * Authentication ticket type - Header value required by back-end service
     * Specifies what type of authentication to do
     */
    export const authenticationTicketType = "10";

    /**
     * Header name that holds which ANON cookie to use
     */
    export const signedInCookieNameHeader = "signedInCookieName";

    /**
     * Header name that holds the user-location value.
     */
    export const userLocationHeader = "user-location";

    /**
     * MUID returned from Windows api.
     */
    let windowsMuid: string;

    /**
     * Executes a callback on completing of a service request
     *
     * @param {() => Promise<T>} serviceRequest - The service request
     * @param {() => void} callbackMethod - The callback method
     * @return {Promise<Response>} - A promise of the service response
     */
    export async function serviceRequestOnComplete<T>(serviceRequest: () => Promise<T>, callbackMethod: () => void): Promise<T> {
        try {
            const result = await serviceRequest();
            callbackMethod();

            return result;
        } catch (error) {
            callbackMethod();
            throw error;
        }
    }

    /**
     * Gets common parameters used in service requests to OneService, when there is no personalization involved
     * @param {string} userId - The user id for the request
     * @param {string} ocidSpecifier - The OCID
     * @param {string} addPageInfoToOcid - If true, page_product and page_type are added to the ocid. Defaults to true
     * @param {string} flightData - Additional flight ExPs to send to OneService. e.g. to tell OneService to return Bing Image urls.
     *                              NOTE: make sure WW uses the same
     * @param {boolean} useEntCompliance - Should we use enterprise compliant params.
     * @returns {KeyValueArray<string, string>}
     */
    export function getOneServiceParamsWithoutAuth(
        userId: string = null,
        ocidSpecifier: string,
        addPageInfoToOcid = true,
        flightData: string = null,
        overwriteFlightData = false,
        useEntCompliance = useEnterpriseCompliance()): KeyValueArray<string, string> {
        const appEnvironment = getAppEnvironment();
        const params = [
            {
                key: oneServiceQueryStringKeys.apiKey,
                value: appEnvironment.OneServiceApiKey
            },
            {
                // TODO Bug 22266910: clarify use of activityId vs rid in this spot
                key: oneServiceQueryStringKeys.activityId,
                value: appEnvironment.ActivityId
            },
            {
                key: oneServiceQueryStringKeys.ocid,
                value: addPageInfoToOcid ? getOneServiceOCID(appEnvironment.TrackInfo, ocidSpecifier) : ocidSpecifier
            },
            {
                key: oneServiceQueryStringKeys.market,
                value: appEnvironment.CurrentMarket
            }
        ];

        // 21527208 - remove this after upgrading experiences to use shared lib.
        if (userId) {
            params.push({
                key: oneServiceQueryStringKeys.user,
                value: userId
            });
        }

        let runningExperiments = getHeadData().CurrentFlights;
        if (flightData) {
            runningExperiments = !runningExperiments ? flightData : `${runningExperiments},${flightData}`;

            const bingImageResizerFlight = "prg-bngimg-t";
            if (runningExperiments.indexOf(bingImageResizerFlight) === -1) {
                runningExperiments += "," + bingImageResizerFlight;
            }

            if (overwriteFlightData === true) {
                runningExperiments = flightData;   // Overwrites when flag is on.
            }
        }
        if (runningExperiments) {
            params.push({
                key: oneServiceQueryStringKeys.fdhead,
                value: runningExperiments
            });
        }

        if (useEntCompliance) {
            params.push({
                key: aadTokenQsKey,
                value: aadTokenQsVal
            });
        }

        if (headData.IsBlendedEnterprise) {
            params.push({
                key: "audienceMode",
                value: "enterprise"
            });
        }

        return params;
    }

    /**
     * Returns the params which are static in nature and don't change.
     * @param ocidSpecifier - The OCID
     * @param addPageInfoToOcid - If true, page_product and page_type are added to the ocid. Defaults to true
     */
    export function getOneServiceNonDynamicParamsWithoutAuth(ocidSpecifier: string, addPageInfoToOcid = true): KeyValueArray<string, string> {
        const appEnvironment = getAppEnvironment();
        const params = [
            {
                key: oneServiceQueryStringKeys.apiKey,
                value: appEnvironment.OneServiceApiKey
            },
            {
                key: oneServiceQueryStringKeys.ocid,
                value: addPageInfoToOcid ? getOneServiceOCID(appEnvironment.TrackInfo, ocidSpecifier) : ocidSpecifier
            },
            {
                key: oneServiceQueryStringKeys.market,
                value: appEnvironment.CurrentMarket
            }
        ];

        if (useEnterpriseCompliance()) {
            params.push({
                key: aadTokenQsKey,
                value: aadTokenQsVal
            });
        }

        return params;
    }

    /**
     * Gets common parameters used in service requests to OneService,
     * plus authentication params in the form of the sign in cookie name
     * Note: This should only be called if the user is known to be signed in, else use getOneServiceParamsWithoutAuth
     *
     * @export
     * @param {string} userId - The user id for the request
     * @param {string} ocidSpecifier - The OCID
     * @param {string} addPageInfoToOcid - If true, page_product and page_type are added to the ocid. Defaults to true
     * @param {string} flightData - Additional flight ExPs to send to OneService. e.g. to tell OneService to return Bing Image urls.
     * @returns {KeyValueArray<string, string>}
     */
    export function getOneServiceParamsWithAuth(userId: string = null, ocidSpecifier: string, addPageInfoToOcid = true,
        flightData: string = null, overwriteFlightData = false): KeyValueArray<string, string> {
        const appEnvironment = getAppEnvironment();
        const authCookieName: string = appEnvironment.AuthCookieName as string;
        const params = [...getOneServiceParamsWithoutAuth(userId, ocidSpecifier, addPageInfoToOcid, flightData, overwriteFlightData)];
        if (authCookieName) {
            params.push({
                key: oneServiceQueryStringKeys.signInCookieName, // sign-in cookie name
                value: authCookieName.toUpperCase()
            });
        }

        return params;
    }

    /**
     * Gets common headers used in service requests to OneService
     * @returns The headers to use on the OneService call.
     */
    export async function getOneServiceHeaders(useEntCompliance = useEnterpriseCompliance(), useEntAuthHeader?: boolean): Promise<Record<string, string>> {
        const headers: Record<string, string> = {};

        if (useEntCompliance && !await isGccModUser()) {
            const aadData = await getAadData(clientId, oneServiceScope, oneServiceFriendlyName);
            if (aadData && aadData.token && useEntAuthHeader) {
                headers[oneServiceHeaders.entAuthorization] = aadData.token;
            } else if (aadData && aadData.token) {
                headers[oneServiceHeaders.authorization] = `Bearer ${aadData.token}`;
            }
        }

        // For SSR, pass cookies and referer from the original request to the OneService request
        // Both cookie and referer will use for feed call to evaluate the sign-in status and get the personalized river.
        if (!canUseDOM()) {
            const cookieString = getRequestData().cookie;
            if (cookieString) {
                headers.Cookie = cookieString;
            }
            const refererString = getRequestData().referer;
            if (refererString) {
                headers.Referer = refererString;
            }
        }

        return headers;
    }

    /**
     * Gets common headers used in service requests to OneService in Windows Widget
     * @param widgetInstance Instance of widget
     * @param useAadHeaders Indicates whether AAD headers should be added for an AAD user
     * @returns The headers to use on the OneService call.
     */
    export async function getWinWidgetOneServiceHeaders(widgetInstance: IWidgetInstance, useAadHeaders = false, widgetInstanceId?: string, sessionId?: string): Promise<Record<string, string>> {
        const appEnvironment = getAppEnvironment();
        const audienceMode = appEnvironment?.CurrentRequestTargetScope?.audienceMode;
        const isAadUser = audienceMode === AudienceModeType.Enterprise;

        const headers: Record<string, string> = {};

        if (!widgetInstance) {
            Telemetry.sendAppErrorEvent({
                ...FailedToRetrieveUserAuthTokenAndMUID,
                message: "Failed to retrieve user auth token and MUID from WDX client due to undefined widget instance",
                source: "OneSericeUtility",
                pb: {
                    ...FailedToRetrieveUserAuthTokenAndMUID.pb,
                    customMessage: `widgetInstanceId ${widgetInstanceId} sessionId ${sessionId} widgetInstance ${widgetInstance}`
                }
            });

            return headers;
        }

        let authToken: string | undefined;

        const widgetTokenPromise = getWindowsWidgetToken(
            widgetInstance,
            isAadUser ? winWidClientIdAad : winWidClientIdMsa,
            isAadUser ? winWidScopeAad : winWidScopeMsa,
            isAadUser ? winWidResourceAad : winWidResourceMsa
        );

        if (windowsMuid) {
            authToken = await widgetTokenPromise;
        } else {
            [authToken, windowsMuid] = await Promise.all([widgetTokenPromise, getWindowsWidgetMuid(widgetInstance)]);
        }

        if (authToken) {
            // AAD
            if (isAadUser && useAadHeaders) {
                headers[oneServiceHeaders.entAuthorization] = authToken;
            }

            // MSA
            if (!isAadUser) {
                headers[oneServiceHeaders.rpsToken] = authToken;
            }
        } else {
            Telemetry.sendAppErrorEvent({
                ...FailedToRetrieveAuthTokenFromWDXClient,
                message: "WDX Client failed to return user auth token",
                source: "OneSericeUtility",
                pb: {
                    ...FailedToRetrieveAuthTokenFromWDXClient.pb,
                    customMessage: `widgetInstanceId ${widgetInstanceId} sessionId ${sessionId} widgetInstance ${widgetInstance}`
                }
            });
        }

        const muidCookie = getMuidCookie();
        if (windowsMuid) {
            headers[oneServiceHeaders.muid] = windowsMuid;
            if (windowsMuid !== muidCookie) {
                // Sync the cookie with MUID from Windows
                setMuidCookie(windowsMuid);
            }
        } else if (muidCookie) {
            Telemetry.sendAppErrorEvent({
                ...FailedToRetrieveMUIDFromWDXClient,
                message: "WDX Client failed to return MUID",
                source: "OneSericeUtility",
                pb: {
                    ...FailedToRetrieveMUIDFromWDXClient.pb,
                    customMessage: `widgetInstanceId ${widgetInstanceId} sessionId ${sessionId} widgetInstance ${widgetInstance}`
                }
            });

            headers[oneServiceHeaders.muid] = muidCookie;
        }

        return headers;
    }

    /**
     * Gets headers specifically used in service requests to OneService /feed endpoint
     * @export
     * @returns The headers to use for the call.
     */
    export async function getOneServiceFeedCallHeaders(useEntCompliance?: boolean, useEntAuthHeader?: boolean): Promise<Record<string, string>> {
        const headers = await getOneServiceHeaders(useEntCompliance, useEntAuthHeader);
        const appEnvironment = getAppEnvironment();
        const activityId = appEnvironment.ActivityId;

        if (appEnvironment.SendFeedCallActivityIdInHeader && activityId) {
            headers[oneServiceHeaders.activityId] = activityId;
        }

        return headers;
    }

    /**
     * Gets the location query string parameters based on lat/long from paramaters or AppEnvironment.
     * @param customizedLatitude The customized latitude
     * @param customizedLongitude The customized Longitude
     * @returns The key value pair of location query string parameters.
     */
    export function getLocationParams(customizedLatitude?: string, customizedLongitude?: string): KeyValuePair<string, string> {
        const appEnvironment = getAppEnvironment();
        const latitude = customizedLatitude || appEnvironment.Latitude;
        const longitude = customizedLongitude || appEnvironment.Longitude;

        if (latitude && longitude) {
            return { key: oneServiceQueryStringKeys.location, value: `${latitude}|${longitude}` };
        }

        return null;
    }

    /**
     * Returns the OCID generated from the telemetry TrackInfo if it is valid, otherwise returns the ocid specifier
     * @param trackInfo The track info
     * @param ocidSpecifier The OCID
     * @returns The OCID to use.
     */
    export function getOneServiceOCID(trackInfo: ITrackInfo, ocidSpecifier: string): string {
        if (Utility.isNonNullObject(trackInfo) &&
            Utility.isNonNullObject(trackInfo.sitePage) &&
            isString(trackInfo.sitePage.page_product) &&
            isString(trackInfo.sitePage.page_type)) {
            return trackInfo.sitePage.page_product + "-" + trackInfo.sitePage.page_type + (ocidSpecifier ? "-" + ocidSpecifier : "");
        } else {
            return ocidSpecifier;
        }
    }

    /**
     * Sets up base request data for One Service calls
     * @param method
     * @returns - RequestInit
     */
    export async function getBaseRequestData(method): Promise<RequestInit> {
        const requestObj: RequestInit = {
            headers: await OneServiceUtility.getOneServiceHeaders(),
            method
        };

        return requestObj;
    }

    /**
     * Prefer to use 'getCommonParams2'
     * Populates the request init and return commonParams
     * @param ocid -- the ocid for this request
     * @param {boolean} addBingImageExP - If true, adds the fdhead to ExP list that tells OneService to return Bing Image urls . Defaults to false
     * @returns {KeyValueArray<string, string>} - parameters as a key/value array
     */
    export function getCommonParams(ocid: string, addBingImageExP = false): KeyValueArray<string, string> {
        // TODO: this is buggy, UserIsSignedIn always undefined for Peregrine experience
        // so all calls flow to 'getOneServiceParamsWithoutAuth'
        // need an effort to clean up these usage
        // need to verify the difference between sign-in & non-sign-in
        if (getHeadData().UserIsSignedIn) {
            return this.getOneServiceParamsWithAuth(getAppEnvironment().UserId, ocid, addBingImageExP);
        }

        return this.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, ocid, addBingImageExP);
    }

    /**
     * Populates the request init and return commonParams
     * @param ocid -- the ocid for this request
     * @param {string} addPageInfoToOcid - If true, page_product and page_type are added to the ocid. Defaults to true
     * @param {boolean} addBingImageExP - If true, adds the fdhead to ExP list that tells OneService to return Bing Image urls . Defaults to false
     * @returns {KeyValueArray<string, string>} - parameters as a key/value array
     */
    export function getCommonParams2(ocid: string, addPageInfoToOcid = true, addBingImageExP = false): KeyValueArray<string, string> {
        if (isUserSignedIn()) {
            return this.getOneServiceParamsWithAuth(getAppEnvironment().UserId, ocid, addPageInfoToOcid, addBingImageExP);
        }

        return this.getOneServiceParamsWithoutAuth(getAppEnvironment().UserId, ocid, addPageInfoToOcid, addBingImageExP);
    }

    /**
     * Get whether a given url has an ocid query param
     * @param url url to check for ocid query param
     * @returns boolean indicating whether the given url has an ocid query param
     */
    export function urlContainsOcidParam(url: string): boolean {
        if (!url) {
            return false;
        }

        const split = url.split("?");

        if (split.length < 2) {
            return false;
        }

        const searchString = split[1].split("#")[0];
        const queryParms = new UrlSearchParamsHelper(searchString);
        return !!queryParms.get("ocid");
    }

    /**
     * Remove ocid param from a url
     * @param url url to remove query param from
     * @returns new url without an ocid query param
     */
    export function removeOcidFromUrl(url: string): string {
        if (!url) {
            return url;
        }

        const split = url.split("?");

        if (split.length < 2) {
            return url;
        }

        // If there is a #, remove everything after.
        const searchString = split[1].split("#")[0];
        const queryParams = new UrlSearchParamsHelper(searchString);

        if (queryParams.get("ocid")) {
            queryParams.delete("ocid");
            let result = url.replace(searchString, queryParams.toString());

            if (result.charAt(result.length - 1) === "?") {
                // remove question mark if it's the last character
                result = result.substring(0, result.length - 1);
            }

            return result;
        } else {
            return url;
        }
    }

    /**
     * Remove ocid's from card urls, so telemetry can append a specified ocid
     * @param response - reponse from OneService for River
     * @returns the given reponse with ocid query params removed from each card's destination url
     */
    export function removeOcidFromRiverCardProviderResponse(response: any) {
        if (!response || !response.cardMetadata || !response.cardMetadata.length) {
            return;
        }

        response.cardMetadata.forEach((card) => {
            if (card.cardContent && card.cardContent.destinationUrl) {
                card.cardContent.destinationUrl = OneServiceUtility.removeOcidFromUrl(card.cardContent.destinationUrl);
            } else if (card.subItems && card.subItems.length) {
                card.subItems.forEach(subItem => {
                    if (subItem.cardContent && subItem.cardContent.destinationUrl) {
                        subItem.cardContent.destinationUrl = OneServiceUtility.removeOcidFromUrl(subItem.cardContent.destinationUrl);
                    }
                });
            }
        });
    }

    /**
     * Remove ocid's from card urls, so telemetry can append a specified ocid
     * @param response - reponse from OneService for Windows
     * @returns the given reponse with ocid query params removed from each card's url
     */
    export function removeOcidFromWindowsOneServiceResponse(response: any) {
        if (!response || !response.cards) {
            return;
        }

        response.cards.forEach((card) => {
            card.subCards.forEach((subCard) => {
                if (subCard.url) {
                    subCard.url = OneServiceUtility.removeOcidFromUrl(subCard.url);
                } else if (subCard.subCards) {
                    subCard.subCards.forEach(subSubCard => {
                        if (subSubCard.url) {
                            subSubCard.url = OneServiceUtility.removeOcidFromUrl(subSubCard.url);
                        }
                    });
                }
            });
        });
    }
}